import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Link
} from '@mui/material';
import {
  SystemUpdate as UpdateIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import systemUpdateService from '../services/systemUpdateService';

/**
 * System Update Button Component
 * Provides functionality to check for and apply system updates
 */
const SystemUpdateButton = ({ variant = 'contained', size = 'medium', sx = {} }) => {
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [updateInfo, setUpdateInfo] = useState(null);
  const [updateResult, setUpdateResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check for updates when dialog opens
  useEffect(() => {
    if (updateDialogOpen) {
      checkForUpdates();
    }
  }, [updateDialogOpen]);

  const checkForUpdates = async () => {
    setLoading(true);
    setError(null);
    setUpdateInfo(null);
    setUpdateResult(null);

    try {
      const result = await systemUpdateService.checkForUpdates();
      
      if (result.success) {
        setUpdateInfo(result.data);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to check for updates');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSystem = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await systemUpdateService.updateSystem();
      
      if (result.success) {
        setUpdateResult(result.data);
        // Refresh update info after successful update
        setTimeout(() => {
          checkForUpdates();
        }, 1000);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to update system');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setUpdateDialogOpen(false);
    setUpdateInfo(null);
    setUpdateResult(null);
    setError(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        startIcon={<UpdateIcon />}
        onClick={() => setUpdateDialogOpen(true)}
        sx={sx}
      >
        System Update
      </Button>

      <Dialog
        open={updateDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { minHeight: '60vh' }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          pb: 1
        }}>
          <UpdateIcon color="primary" />
          <Typography variant="h5" component="h1">
            System Update
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {updateResult && (
            <Alert 
              severity={updateResult.success ? "success" : "error"} 
              sx={{ mb: 2 }}
              icon={updateResult.success ? <CheckIcon /> : <ErrorIcon />}
            >
              <Typography variant="subtitle2" gutterBottom>
                {updateResult.success ? 'Update Successful' : 'Update Failed'}
              </Typography>
              <Typography variant="body2">
                {updateResult.message}
              </Typography>
              {updateResult.restartRequired && (
                <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                  ⚠️ System restart required
                </Typography>
              )}
            </Alert>
          )}

          {loading && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              minHeight: 200 
            }}>
              <CircularProgress />
            </Box>
          )}

          {updateInfo && !loading && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Typography variant="h6">Current Version:</Typography>
                <Chip 
                  label={updateInfo.currentVersion} 
                  color="default" 
                  variant="outlined"
                />
                <Typography variant="h6">Latest Version:</Typography>
                <Chip 
                  label={updateInfo.latestVersion} 
                  color={updateInfo.updateAvailable ? "primary" : "default"}
                  variant={updateInfo.updateAvailable ? "filled" : "outlined"}
                />
              </Box>

              {updateInfo.updateAvailable ? (
                <Alert severity="info" sx={{ mb: 2 }} icon={<InfoIcon />}>
                  <Typography variant="subtitle2" gutterBottom>
                    Update Available
                  </Typography>
                  <Typography variant="body2">
                    A new version ({updateInfo.latestVersion}) is available. 
                    Your current version is {updateInfo.currentVersion}.
                  </Typography>
                </Alert>
              ) : (
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    System Up to Date
                  </Typography>
                  <Typography variant="body2">
                    You are running the latest version ({updateInfo.currentVersion}).
                  </Typography>
                </Alert>
              )}

              {updateInfo.releaseNotes && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Release Notes:
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    whiteSpace: 'pre-wrap',
                    backgroundColor: 'action.hover',
                    p: 2,
                    borderRadius: 1
                  }}>
                    {updateInfo.releaseNotes}
                  </Typography>
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Release Date: {formatDate(updateInfo.releaseDate)}
                </Typography>
                {updateInfo.releaseUrl && (
                  <Link 
                    href={updateInfo.releaseUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    variant="body2"
                  >
                    View Release
                  </Link>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDialog}>
            Close
          </Button>
          {updateInfo?.updateAvailable && !updateResult && (
            <Button
              onClick={handleUpdateSystem}
              variant="contained"
              color="primary"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <UpdateIcon />}
            >
              {loading ? 'Updating...' : 'Update System'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SystemUpdateButton;
